import { type FormEvent, useState } from 'react'

import { Modal } from './Modal'
import { PasswordField } from './PasswordField'

/** Pantalla de "confirmá tu contraseña" que dispara `useWithStepUp` cuando el
 *  backend rechaza una acción sensible por falta de re-autenticación reciente.
 */
export function StepUpDialog({
  onCancel,
  onConfirm,
  isPending,
  error,
  reason = 'Esta acción maneja una credencial de exchange — hace falta reingresarla, aunque la sesión siga activa.',
}: {
  onCancel: () => void
  onConfirm: (password: string) => void | Promise<void>
  isPending: boolean
  error: Error | null
  reason?: string
}) {
  const [password, setPassword] = useState('')

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    try {
      await onConfirm(password)
    } catch {
      // `useWithStepUp.confirm` ya guarda el error (de /auth/step-up o del
      // reintento de la acción original) en su propio estado, expuesto acá
      // vía la prop `error` — sin este catch era una unhandled promise
      // rejection en la consola por cada contraseña rechazada o reintento
      // fallido.
    }
  }

  return (
    <Modal onClose={onCancel}>
      <form onSubmit={handleSubmit}>
        <h3>Confirmá tu contraseña</h3>
        <p className="muted">{reason}</p>

        {error && (
          <div className="alert" role="alert">
            {error.message}
          </div>
        )}

        <PasswordField
          id="step-up-password"
          label="Contraseña"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          autoFocus
          required
        />

        <div className="confirm-dialog__actions">
          <button className="button button--ghost" type="button" onClick={onCancel}>
            Cancelar
          </button>
          <button className="button" type="submit" disabled={isPending}>
            {isPending ? 'Confirmando…' : 'Confirmar'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
